import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as artifactService from '../../services/artifactService';
import { useTheme } from '../../contexts/ThemeContext';

interface ImageDiffViewerProps {
  artifactId: string;
  versionA: string;
  versionB: string;
}

type ViewMode = 'side-by-side' | 'slider' | 'heatmap' | 'diff';

export const ImageDiffViewer: React.FC<ImageDiffViewerProps> = ({
  artifactId,
  versionA,
  versionB
}) => {
  const { darkMode } = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('side-by-side');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [imageA, setImageA] = useState<string>('');
  const [imageB, setImageB] = useState<string>('');

  const { data: diffData, isLoading, error } = useQuery(
    ['imageArtifactDiff', artifactId, versionA, versionB],
    () => artifactService.compareImageArtifacts(artifactId, versionA, versionB)
  );
  
  // Fetch image URLs at component top level (fixes Rules of Hooks violation)
  useEffect(() => {
    const fetchImages = async () => {
      if (diffData) {
        const urlA = await artifactService.getArtifactVersionImageUrl(artifactId, diffData.version_a.id);
        const urlB = await artifactService.getArtifactVersionImageUrl(artifactId, diffData.version_b.id);
        setImageA(urlA);
        setImageB(urlB);
      }
    };
    fetchImages();
  }, [diffData, artifactId]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderPosition(Number(e.target.value));
  };
  
  const renderSideBySide = () => {
    if (!diffData || !diffData.diff_result.images.side_by_side) {
      return <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Side-by-side comparison not available</div>;
    }
    return (
      <div className="flex justify-center">
        <img
          src={diffData.diff_result.images.side_by_side}
          alt="Side by side comparison"
          className="max-w-full max-h-[600px] object-contain"
        />
      </div>
    );
  };
  
  const renderSlider = () => {
    if (!diffData) return null;

    if (!imageA || !imageB) {
      return <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading images...</div>;
    }

    return (
      <div className="slider-comparison relative">
        <div className="relative overflow-hidden">
          <img src={imageB} alt="Version B" className="max-w-full max-h-[600px] object-contain" />
          <div
            className="absolute top-0 left-0 right-0 bottom-0 overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
          >
            <img src={imageA} alt="Version A" className="max-w-full max-h-[600px] object-contain" />
          </div>
          <div
            className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
            style={{ left: `${sliderPosition}%`, boxShadow: '0 0 5px rgba(0,0,0,0.5)' }}
          />
        </div>
        <div className="mt-4">
          <input
            type="range"
            min="0"
            max="100"
            value={sliderPosition}
            onChange={handleSliderChange}
            className="w-full"
          />
          <div className={`flex justify-between text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <span>Version A ({diffData.version_a.number})</span>
            <span>Version B ({diffData.version_b.number})</span>
          </div>
        </div>
      </div>
    );
  };
  
  const renderHeatmap = () => {
    if (!diffData || !diffData.diff_result.images.heatmap) {
      return <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Heatmap not available</div>;
    }
    return (
      <div className="flex flex-col items-center">
        <img
          src={diffData.diff_result.images.heatmap}
          alt="Difference heatmap"
          className="max-w-full max-h-[600px] object-contain"
        />
        <div className={`mt-4 flex items-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <div className="flex items-center mr-4">
            <span className="w-4 h-4 inline-block bg-blue-500 mr-1" />
            <span>Small difference</span>
          </div>
          <div className="flex items-center mr-4">
            <span className="w-4 h-4 inline-block bg-green-500 mr-1" />
            <span>Medium difference</span>
          </div>
          <div className="flex items-center mr-4">
            <span className="w-4 h-4 inline-block bg-yellow-500 mr-1" />
            <span>Large difference</span>
          </div>
          <div className="flex items-center">
            <span className="w-4 h-4 inline-block bg-red-500 mr-1" />
            <span>Major difference</span>
          </div>
        </div>
      </div>
    );
  };

  const renderDiff = () => {
    if (!diffData || !diffData.diff_result.images.diff) {
      return <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Difference visualization not available</div>;
    }
    return (
      <div className="flex justify-center">
        <img
          src={diffData.diff_result.images.diff}
          alt="Difference visualization"
          className="max-w-full max-h-[600px] object-contain"
        />
      </div>
    );
  };
  
  if (isLoading) {
    return <div className={`text-center py-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading image comparison...</div>;
  }

  if (error) {
    return (
      <div className={`text-center py-4 rounded-lg p-4 ${
        darkMode ? 'bg-red-900/20 border border-red-700 text-red-400' : 'text-red-600'
      }`}>
        Error loading image comparison
      </div>
    );
  }

  return (
    <div className="image-diff-viewer">
      <div className={`mb-4 p-4 rounded-lg border ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white shadow border-transparent'
      }`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Comparing Images</h3>
            <div className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {diffData?.version_a.number} ({diffData?.version_a.agent_name}) → {diffData?.version_b.number} ({diffData?.version_b.agent_name})
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              View Mode
            </label>
            <div className="flex space-x-2">
              {(['side-by-side', 'slider', 'heatmap', 'diff'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  className={`px-3 py-1 text-sm rounded ${
                    viewMode === mode
                      ? 'bg-blue-600 text-white'
                      : darkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => setViewMode(mode)}
                >
                  {mode === 'side-by-side' ? 'Side by Side' : mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {diffData && (
          <div className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="font-medium">Similarity:</span> {diffData.diff_result.similarity.toFixed(2) * 100}%
            <span className="ml-4 font-medium">Difference:</span> {diffData.diff_result.difference_percentage}%
          </div>
        )}
      </div>

      <div className={`rounded-lg border p-4 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white shadow border-transparent'
      }`}>
        {viewMode === 'side-by-side' && renderSideBySide()}
        {viewMode === 'slider' && renderSlider()}
        {viewMode === 'heatmap' && renderHeatmap()}
        {viewMode === 'diff' && renderDiff()}
      </div>
    </div>
  );
};
